/**
 * OrderStatus — Customer-Facing Kitchen Status Board  v2
 * ──────────────────────────────────────────────────────
 * Real-time 4-column board mirroring the actual kitchen workflow.
 *
 * COLUMNS (mapped to real Firestore data from TicketDashboard):
 *   1. ORDER RECEIVED   → order arrived, nothing assigned yet
 *   2. WITH CHEF        → assign button clicked (assigned:true) — order sent to kitchen
 *   3. BEING PACKED     → all chef items done (prepCompletedAt set OR chefDoneItems full)
 *   4. READY FOR PICKUP → all checkedItems done OR manuallyCompleted
 *
 * Data sources:
 *   • /api/square-tickets          – live Square orders (ticketName, items)
 *   • Firestore ticketChecks/{id}  – kitchen state — subscribes per-doc like TicketDashboard
 *
 * Stage rules (always highest wins):
 *   ready    ← manuallyCompleted OR checkedItems.length >= itemCount
 *   packing  ← prepCompletedAt SET  OR  chefDoneItems.length >= itemCount
 *   withchef ← assigned === true    OR  assignedItems.length > 0  OR  chefDoneItems.length > 0
 *   received ← (nothing above)
 */
import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from 'react';
import { Navigate } from 'react-router-dom';
import { doc, onSnapshot, setDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { STORE_HOURS } from '../../constants';
import { useOrderSignal, SIGNAL_COALESCE_MS } from '../Tickets/hooks/useOrderSignal';
import { useTicketChecks } from '../Tickets/hooks/useTicketChecks';
import { isOnlineTicketName } from '../../utils/onlineTicketName';
import './OrderStatus.css';

const ALLOWED_ROLES   = ['owner', 'admin', 'manager', 'security'];
const POLL_MS         = 10_000;
const READY_LINGER_MS = 45 * 60 * 1000;
const TICK_MS         = 30_000;

// Grace period: keep polling for 15 min after close (last orders may still be in progress)
const CLOSE_GRACE_MS  = 15 * 60 * 1000;

const API_BASE =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:3001'
    : 'https://my-app-ramakrishna5s-projects.vercel.app';

// ── Store hours helpers ─────────────────────────────────────────────────
/**
 * Returns true if the current local time is within any store slot,
 * including a grace period after close so in-progress orders are still tracked.
 */
function isStoreOpen(now = new Date(), graceMs = CLOSE_GRACE_MS) {
  const day   = now.getDay();
  const slots = STORE_HOURS[day] || [];
  const mins  = now.getHours() * 60 + now.getMinutes();
  return slots.some(({ open, close }) => {
    const [oh, om] = open.split(':').map(Number);
    const [ch, cm] = close.split(':').map(Number);
    const openMins  = oh * 60 + om;
    const closeMins = ch * 60 + cm + Math.round(graceMs / 60000);
    return mins >= openMins && mins < closeMins;
  });
}

/** Returns a human-readable string for when the store next opens. */
function nextOpenLabel(now = new Date()) {
  const totalMins = now.getHours() * 60 + now.getMinutes();
  const day = now.getDay();
  // Check remaining slots today
  const todaySlots = STORE_HOURS[day] || [];
  for (const { open } of todaySlots) {
    const [oh, om] = open.split(':').map(Number);
    const openMins = oh * 60 + om;
    if (openMins > totalMins) {
      const h = oh % 12 || 12;
      const ampm = oh >= 12 ? 'PM' : 'AM';
      return `Today ${h}:${String(om).padStart(2,'0')} ${ampm}`;
    }
  }
  // Check next 7 days
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  for (let d = 1; d <= 7; d++) {
    const nextDay = (day + d) % 7;
    const slots   = STORE_HOURS[nextDay] || [];
    if (slots.length > 0) {
      const { open } = slots[0];
      const [oh, om] = open.split(':').map(Number);
      const h = oh % 12 || 12;
      const ampm = oh >= 12 ? 'PM' : 'AM';
      return `${days[nextDay]} ${h}:${String(om).padStart(2,'0')} ${ampm}`;
    }
  }
  return 'Soon';
}

/** Returns estimated time for a column based on total item count across its orders. */
function colEta(stage, orders) {
  if (orders.length === 0) return null;
  const total = orders.reduce((s, o) => s + (o.items?.length || 1), 0);
  if (stage === 'withchef') return total < 15 ? '~7-9 min' : '~10-12 min';
  if (stage === 'packing')  return total < 15 ? '~2-3 min' : '~3-4 min';
  return null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function tsToDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  return new Date(ts);
}
function elapsedMin(iso) { return iso ? (Date.now() - new Date(iso).getTime()) / 60000 : 0; }
function elapsedLabel(iso) {
  const m = Math.floor(elapsedMin(iso));
  if (m < 1) return '< 1m';
  if (m < 60) return `${m}m`;
  return `${Math.floor(m/60)}h ${m%60}m`;
}
function ageBand(iso) {
  const m = elapsedMin(iso);
  if (m < 10) return 'fresh';
  if (m < 20) return 'normal';
  if (m < 30) return 'warn';
  return 'urgent';
}

/**
 * Derive the highest-priority stage from a Firestore ticketChecks document.
 *
 * Matches EXACTLY the data written by TicketDashboard:
 *   sqFsAssign          → assigned:true, assignedItems:[all indices]
 *   sqFsToggleChefDoneItem → chefDoneItems:[...], prepCompletedAt (when all done)
 *   sqFsToggleItem      → checkedItems:[...], completedAt (when all done)
 *   sqFsComplete        → manuallyCompleted:true
 *
 * Rules (checked highest → lowest; first match wins):
 *   ready    ← manuallyCompleted  OR  checkedItems.length >= itemCount
 *   packing  ← prepCompletedAt is set  OR  chefDoneItems.length >= itemCount
 *   withchef ← assigned===true AND all items assigned (assignedItems.length >= itemCount)
 *              OR all chef items done (chefDoneItems.length >= itemCount)
 *   received ← (nothing above, incl. partial assign)
 *
 * Partial assign (some but not all items assigned) stays in RECEIVED
 * and the card shows "X of Y assigned" so staff know it's being processed.
 */
function deriveStage(check, itemCount) {
  if (!check) return 'received';

  const checkedItems  = Array.isArray(check.checkedItems)  ? check.checkedItems  : [];
  const chefDoneItems = Array.isArray(check.chefDoneItems) ? check.chefDoneItems : [];
  const assignedItems = Array.isArray(check.assignedItems) ? check.assignedItems : [];

  // READY: front-desk confirmed pickup
  if (check.manuallyCompleted) return 'ready';
  if (itemCount > 0 && checkedItems.length >= itemCount) return 'ready';

  // PACKING: chef fully done, now being packed by front desk
  if (check.prepCompletedAt) return 'packing';
  if (itemCount > 0 && chefDoneItems.length >= itemCount) return 'packing';

  // WITH CHEF: only when ALL items have been assigned to kitchen
  // (assigned===true via Assign All button, which sets assignedItems to all indices)
  // Also moves here if chef has started individual items done
  const allAssigned = check.assigned === true &&
    (itemCount === 0 || assignedItems.length >= itemCount);
  if (allAssigned) return 'withchef';
  if (chefDoneItems.length > 0) return 'withchef';

  // RECEIVED: partial assign or nothing done yet
  return 'received';
}

// ── Fetch Square orders ───────────────────────────────────────────────────────
// Uses the same /api/square-tickets endpoint as TicketDashboard.
// The API already filters to today's CST bounds — no client-side date filter needed.
// Also refetches the moment the webhook signals a change (useOrderSignal, same as KDS).
function useSquareOrders(paused) {
  const [orders,      setOrders]      = useState([]);
  const [lastFetch,   setLastFetch]   = useState(null);
  const [spinning,    setSpinning]    = useState(false);
  const [fetchError,  setFetchError]  = useState(null);
  const mountedRef    = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  // Folded into the existing `paused` gate rather than run as a second mechanism.
  // A customer who leaves this tab open — the normal way people wait for food —
  // otherwise polls both endpoints forever behind a screen nobody is reading.
  const [visible, setVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState !== 'hidden');
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onChange = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  const idle = paused || !visible;

  // `afterMs` (webhook refetches): skip a server cache older than the change.
  const doFetch = useCallback(async (afterMs) => {
    try {
      const qs   = Number.isFinite(afterMs) && afterMs > 0 ? `?after=${Math.floor(afterMs)}` : '';
      const res  = await fetch(`${API_BASE}/api/square-tickets${qs}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      if (!mountedRef.current) return;
      // API already returns today's CST orders — no client-side date filter needed
      // (applying isToday() here would incorrectly drop evening orders whose UTC
      //  date has already rolled over to the next calendar day)
      const sorted = (json.orders || [])
        .slice()
        .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      setOrders(sorted);
      setLastFetch(new Date());
      setFetchError(null);
    } catch (e) {
      if (mountedRef.current) setFetchError(e.message);
    }
  }, []);

  // Manual refresh with spin animation
  const manualRefresh = useCallback(async () => {
    setSpinning(true);
    await doFetch();
    setTimeout(() => setSpinning(false), 600);
  }, [doFetch]);

  // Poll every 10 s as safety net (also fires immediately on mount)
  useEffect(() => {
    if (idle) return;
    doFetch();
    const id = setInterval(doFetch, POLL_MS);
    return () => clearInterval(id);
  }, [doFetch, idle]);

  // Refetch the moment the webhook signals an order change — a Firestore push
  // (falls back to polling /api/order-signal), shared with the KDS so both boards
  // react at the same speed. Idle (closed / hidden) costs nothing.
  // Called with the change time → doFetch(afterMs); one call per burst of events.
  useOrderSignal(!idle, doFetch, { coalesceMs: SIGNAL_COALESCE_MS });

  return { orders, lastFetch, spinning, manualRefresh, fetchError };
}

// ── ticketChecks for every order — the same batched listeners as the KDS ───────
// (Tickets/hooks/useTicketChecks: a few `documentId() in [...]` queries instead of
// one listener per order, which slowed both boards as the day's orders grew.)

// ── Combine Square + Firestore into staged orders ─────────────────────────────
function useOrderStatus() {
  // Re-check open status every minute
  const [autoOpen,  setAutoOpen]  = useState(() => isStoreOpen());
  const [nextOpen,  setNextOpen]  = useState(() => isStoreOpen() ? null : nextOpenLabel());
  // Manual override: persist across page refreshes
  const [forceOpen, setForceOpen] = useState(() => {
    try { return localStorage.getItem('os_force_open') === '1'; } catch { return false; }
  });
  const toggleForce = useCallback(() => {
    setForceOpen(v => {
      const next = !v;
      try { localStorage.setItem('os_force_open', next ? '1' : '0'); } catch {}
      return next;
    });
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      const open = isStoreOpen();
      setAutoOpen(open);
      setNextOpen(open ? null : nextOpenLabel());
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  const storeOpen = autoOpen || forceOpen;
  const { orders: squareOrders, lastFetch, spinning, manualRefresh, fetchError } = useSquareOrders(!storeOpen);
  const orderIds     = useMemo(() => squareOrders.map(o => o.id), [squareOrders]);
  const checks       = useTicketChecks(orderIds);
  const [readyTs,    setReadyTs]    = useState({});
  const [dismissed,  setDismissed]  = useState(new Set());

  // Load dismissed IDs from Firestore, but only keep ones that exist in today's orders
  // This prevents stale dismissed IDs from hiding genuinely new/active tickets
  useEffect(() => {
    const ref = doc(db, 'orderStatusDismissed', 'board');
    const unsub = onSnapshot(ref, snap => {
      const ids = snap.exists() ? (snap.data().ids || []) : [];
      setDismissed(new Set(ids));
    }, () => {});
    return () => unsub();
  }, []);

  const dismiss = useCallback(async id => {
    setDismissed(prev => { const s = new Set(prev); s.add(id); return s; });
    await setDoc(
      doc(db, 'orderStatusDismissed', 'board'),
      { ids: arrayUnion(id) },
      { merge: true }
    );
  }, []);

  // Mark an order as physically picked up by the customer.
  // Writes pickedUp flag to ticketChecks so the KDS also reflects pickup,
  // then dismisses the card from this board.
  const markPickedUp = useCallback(async id => {
    try {
      await setDoc(
        doc(db, 'ticketChecks', id),
        { pickedUp: true, pickedUpAt: serverTimestamp(), updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (e) {
      console.warn('[OrderStatus] markPickedUp write failed:', e.message);
    }
    dismiss(id);
  }, [dismiss]);

  // Prune dismissed IDs that no longer exist in today's Square orders
  // so re-appearing or genuinely new tickets always show up
  useEffect(() => {
    if (!squareOrders.length || !dismissed.size) return;
    const activeIds = new Set(squareOrders.map(o => o.id));
    const stale = [...dismissed].filter(id => !activeIds.has(id));
    if (!stale.length) return;
    // Remove stale from local state; Firestore cleanup happens at day boundary naturally
    setDismissed(prev => {
      const s = new Set(prev);
      stale.forEach(id => s.delete(id));
      return s;
    });
  }, [squareOrders, dismissed]);

  // Track when each order first entered 'ready' so we can linger then hide.
  // Seed timestamp from Firestore completedAt → createdAt → now (in that priority order)
  // so orders that were already done before the page loaded expire correctly.
  useEffect(() => {
    setReadyTs(prev => {
      let changed = false;
      const next  = { ...prev };
      squareOrders.forEach(o => {
        const check = checks[o.id];
        if (!check && next[o.id]) { delete next[o.id]; changed = true; return; }
        const stage = deriveStage(check, (o.items || []).length);
        if (stage === 'ready' && !next[o.id]) {
          // Priority: Firestore completedAt > Square closedAt > Square createdAt > now
          const fsCompleted = check?.completedAt ? tsToDate(check.completedAt) : null;
          const sqClosed    = o.closedAt  ? new Date(o.closedAt)  : null;
          const sqCreated   = o.createdAt ? new Date(o.createdAt) : null;
          const anchor      = fsCompleted || sqClosed || sqCreated || new Date();
          next[o.id] = anchor.getTime();
          changed = true;
        }
        if (stage !== 'ready' && next[o.id]) { delete next[o.id]; changed = true; }
      });
      return changed ? next : prev;
    });
  }, [squareOrders, checks]);

  const orders = useMemo(() => {
    const now    = Date.now();
    const result = squareOrders.map(o => {
      const check     = checks[o.id];
      const itemCount = (o.items || []).length;
      const stage     = deriveStage(check, itemCount);
      const rTs           = readyTs[o.id];
      // squareDone: paid (COMPLETED state), discount applied, explicit paid flag, or online order
      const squareDone    = o.state === 'COMPLETED' || !!o.paid || !!o.hasDiscount
                            || isOnlineTicketName(o.ticketName);
      // Auto-hide #1: Square paid/discounted/online + KDS ready = order is fully done, no need to show
      if (stage === 'ready' && squareDone) return null;
      // Auto-hide #2: order has been in ready state longer than the linger window
      if (stage === 'ready' && rTs && now - rTs > READY_LINGER_MS) return null;
      // Auto-hide #3: manually dismissed by staff
      if (stage === 'ready' && dismissed.has(o.id)) return null;
      return {
        id:              o.id,
        ticketName:      o.ticketName || o.id.slice(-4).toUpperCase(),
        items:           o.items || [],
        createdAt:       o.createdAt,
        stage,
        chefDoneItems:    check?.chefDoneItems  || [],
        checkedItems:     check?.checkedItems   || [],
        assignedItems:    check?.assignedItems  || [],
        assignedCount:    (check?.assignedItems || []).length,
        prepCompletedAt:  check?.prepCompletedAt || null,
        // Payment / discount info from Square
        paid:             !!o.paid,
        hasDiscount:      !!o.hasDiscount,
        totalDiscountCents: o.totalDiscountCents || 0,
        total:            o.total || 0,
      };
    }).filter(Boolean);

    return result;
  }, [squareOrders, checks, readyTs, dismissed]);

  return {
    received: orders.filter(o => o.stage === 'received'),
    withchef: orders.filter(o => o.stage === 'withchef'),
    packing:  orders.filter(o => o.stage === 'packing'),
    ready:    orders.filter(o => o.stage === 'ready')
                    .sort((a, b) => (readyTs[b.id] || 0) - (readyTs[a.id] || 0)),
    total:    orders.length,
    dismiss,
    markPickedUp,
    storeOpen,
    forceOpen,
    toggleForce,
    nextOpen,
    lastFetch,
    spinning,
    manualRefresh,
    fetchError,
  };
}

// ── Order Card ────────────────────────────────────────────────────────────────
function OrderCard({ order, onDismiss, onPickedUp }) {
  const [entered,    setEntered]    = useState(false);
  const [dismissing, setDismissing] = useState(false);
  useEffect(() => { const t = setTimeout(() => setEntered(true), 40); return () => clearTimeout(t); }, []);

  const elapsed   = elapsedLabel(order.createdAt);
  const band      = ageBand(order.createdAt);
  const itemCount = order.items.length;
  const isReady   = order.stage === 'ready';

  const handlePickedUp = () => {
    setDismissing(true);
    setTimeout(() => (onPickedUp || onDismiss)(order.id), 350);
  };

  // Build a compact single-line item summary
  const firstItem   = order.items[0];
  const extraCount  = itemCount - 1;
  const itemSummary = firstItem
    ? (firstItem.qty > 1 ? `${firstItem.qty}× ${firstItem.name}` : firstItem.name)
    + (extraCount > 0 ? ` +${extraCount} more` : '')
    : null;

  const chefProgress = order.stage === 'withchef' && itemCount > 0
    ? `${order.chefDoneItems.length}/${itemCount} prepped`
    : null;
  const packProgress = order.stage === 'packing' && itemCount > 0
    ? `${order.checkedItems.length}/${itemCount} packed`
    : null;
  const partialAssign = order.stage === 'received' && itemCount > 0 && order.assignedCount > 0
    ? `${order.assignedCount} of ${itemCount} assigned`
    : null;

  // Payment / discount pill (shown on all stages)
  const discountLabel = order.hasDiscount && order.totalDiscountCents > 0
    ? `-$${(order.totalDiscountCents / 100).toFixed(2)}`
    : null;

  return (
    <div className={`os-card os-card--${order.stage} os-card--age-${band} ${entered ? 'os-card--entered' : ''} ${dismissing ? 'os-card--dismissing' : ''}`}>
      {isReady && <div className="os-card__ready-ring" aria-hidden="true" />}

      {/* Top row: name + elapsed + payment badge */}
      <div className="os-card__name-row">
        <span className="os-card__name">{order.ticketName}</span>
        <div className="os-card__name-row-right">
          {order.paid && (
            <span className="os-card__payment-badge os-card__payment-badge--paid" title="Payment received in Square">
              💳 Paid
            </span>
          )}
          {order.hasDiscount && (
            <span className="os-card__payment-badge os-card__payment-badge--discount" title={`Discount applied${discountLabel ? ': ' + discountLabel : ''}`}>
              🎟 {discountLabel || 'Discount'}
            </span>
          )}
          <span className={`os-card__elapsed os-card__elapsed--${band}`}>{elapsed}</span>
        </div>
      </div>

      {/* Single-line item summary */}
      {itemSummary && (
        <div className="os-card__item-summary">{itemSummary}</div>
      )}

      {/* Footer row */}
      <div className="os-card__footer">
        {isReady ? (
          <>
            <span className="os-card__ready-badge">
              <span className="os-card__check-icon">✓</span> Pick up at counter
            </span>
            <button
              className="os-card__dismiss-btn"
              onClick={handlePickedUp}
              title="Mark as picked up — records pickup in KDS"
            >
              ✓ Picked Up
            </button>
          </>
        ) : packProgress ? (
          <span className="os-card__progress os-card__progress--pack">📦 {packProgress}</span>
        ) : chefProgress ? (
          <span className="os-card__progress os-card__progress--chef">👨‍🍳 {chefProgress}</span>
        ) : partialAssign ? (
          <span className="os-card__progress os-card__progress--partial">⏳ {partialAssign}</span>
        ) : (
          <span className="os-card__item-count">{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
        )}
        {order.stage === 'withchef' && (
          <span className="os-card__dots" aria-hidden="true"><span /><span /><span /></span>
        )}
      </div>
    </div>
  );
}

// ── Column ────────────────────────────────────────────────────────────────────
function OrderColumn({ stage, icon, label, sublabel, orders, emptyMsg, onDismiss, onPickedUp, etaLabel }) {
  return (
    <div className={`os-col os-col--${stage}`} role="region" aria-label={label}>
      <div className="os-col__header">
        <span className="os-col__icon">{icon}</span>
        <div className="os-col__title-wrap">
          <h2 className="os-col__title">{label}</h2>
          <p className="os-col__sublabel">{sublabel}</p>
        </div>
        {etaLabel
          ? <span className="os-col__eta">⏱ {etaLabel}</span>
          : <span className="os-col__count">{orders.length}</span>
        }
      </div>
      <div className="os-col__body">
        {orders.length === 0
          ? <div className="os-col__empty">{emptyMsg}</div>
          : orders.map(o => <OrderCard key={o.id} order={o} onDismiss={onDismiss} onPickedUp={onPickedUp} />)
        }
      </div>
    </div>
  );
}

// ── Live Clock ────────────────────────────────────────────────────────────────
function LiveClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(id); }, []);
  return (
    <div className="os-clock">
      <span className="os-clock__time">{time.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}</span>
      <span className="os-clock__date">{time.toLocaleDateString([], { weekday:'short', month:'short', day:'numeric' })}</span>
    </div>
  );
}

// ── Ticker ────────────────────────────────────────────────────────────────────
const TICKER_MSGS = [
  '🙏 Thank you for choosing Desi Bites!',
  '🌶️ Every dish is freshly prepared with authentic spices.',
  '👀 Watch for your name above — we\'ll move it to Ready when it\'s done.',
  '📱 Questions? Ask our team at the counter.',
  '🍛 Fresh · Flavorful · Made with Love',
  '⭐ Enjoyed your meal? Leave us a review — it means the world to us!',
];
function Ticker() {
  return (
    <div className="os-ticker" role="marquee">
      <div className="os-ticker__track">
        {[...TICKER_MSGS, ...TICKER_MSGS].map((msg, i) => (
          <span key={i} className="os-ticker__item">{msg}</span>
        ))}
      </div>
    </div>
  );
}

// ── Step indicator ────────────────────────────────────────────────────────────
const STEPS = [
  { stage: 'received', label: 'Received'    },
  { stage: 'withchef', label: 'With Chef'   },
  { stage: 'packing',  label: 'Being Packed'},
  { stage: 'ready',    label: 'Ready!'      },
];
function StepBar() {
  return (
    <div className="os-steps">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.stage}>
          <div className="os-steps__step">
            <span className={`os-steps__dot os-steps__dot--${s.stage}`} />
            <span className="os-steps__label">{s.label}</span>
          </div>
          {i < STEPS.length - 1 && <span className="os-steps__arrow">→</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function OrderStatus() {
  const {
    received, withchef, packing, ready, dismiss, markPickedUp,
    storeOpen, forceOpen, toggleForce, nextOpen, lastFetch, spinning, manualRefresh, fetchError,
  } = useOrderStatus();
  const { role, loading: authLoading }          = useAuth();
  const [fullscreen, setFullscreen]             = useState(false);
  const [tick, setTick]                         = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen().catch(() => {});
    else document.exitFullscreen().catch(() => {});
  }, []);

  useEffect(() => {
    const h = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  if (authLoading) return <div className="os-loading">Loading…</div>;
  if (!role || !ALLOWED_ROLES.includes(role)) return <Navigate to="/login" replace />;

  const lastFetchLabel = lastFetch
    ? lastFetch.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : null;

  return (
    <div className="os-root" data-tick={tick}>

      <header className="os-header">
        <div className="os-header__brand">
          <span className="os-header__logo">🍛</span>
          <div>
            <h1 className="os-header__name">Desi Bites</h1>
            <p className="os-header__tagline">Order Status Board</p>
          </div>
        </div>
        <div className="os-header__center"><StepBar /></div>
        <div className="os-header__right">

          {/* Open / Closed pill */}
          <div className={`os-store-badge os-store-badge--${storeOpen ? 'open' : 'closed'}`}>
            <span className="os-store-badge__dot" />
            {storeOpen ? 'Open' : 'Closed'}
            {forceOpen && <span className="os-store-badge__override">override</span>}
          </div>

          {/* Manual override toggle */}
          <button
            className={`os-btn-override ${forceOpen ? 'os-btn-override--on' : ''}`}
            onClick={toggleForce}
            title={forceOpen ? 'Override active — click to disable and return to schedule' : 'Force open: keep board active outside schedule hours'}
          >
            <span className="os-btn-override__icon">{forceOpen ? '🔓' : '🔒'}</span>
            <span className="os-btn-override__label">{forceOpen ? 'Override ON' : 'Override'}</span>
          </button>

          {/* Refresh button with last-fetch time */}
          <button
            className={`os-btn-refresh ${spinning ? 'os-btn-refresh--spinning' : ''}`}
            onClick={manualRefresh}
            title={lastFetchLabel ? `Last updated ${lastFetchLabel}` : 'Refresh orders'}
            disabled={spinning}
          >
            <span className="os-btn-refresh__icon">↻</span>
            {lastFetchLabel && <span className="os-btn-refresh__time">{lastFetchLabel}</span>}
          </button>

          <LiveClock />
          <button className="os-btn-fullscreen" onClick={toggleFullscreen}
            title={fullscreen ? 'Exit fullscreen' : 'Fullscreen (TV mode)'}>
            <span className="os-btn-fullscreen__icon">⛶</span>
            <span>{fullscreen ? 'Exit' : 'Fullscreen'}</span>
          </button>
        </div>
      </header>

      {/* Fetch error banner */}
      {fetchError && (
        <div className="os-error-banner">
          ⚠ Could not reach order server — retrying… <span className="os-error-banner__detail">{fetchError}</span>
          <button className="os-error-banner__retry" onClick={manualRefresh}>Retry now</button>
        </div>
      )}

      {/* Closed state — board paused, show friendly message */}
      {!storeOpen ? (
        <div className="os-closed">
          <div className="os-closed__icon">🌙</div>
          <h2 className="os-closed__title">Store is Currently Closed</h2>
          <p className="os-closed__sub">Order tracking paused — polling resumes automatically when we open</p>
          {nextOpen && (
            <div className="os-closed__next">
              <span className="os-closed__next-label">Next opening</span>
              <span className="os-closed__next-time">{nextOpen}</span>
            </div>
          )}
          <button className={`os-closed__refresh ${spinning ? 'os-btn-refresh--spinning' : ''}`}
            onClick={manualRefresh} disabled={spinning}>
            <span className="os-btn-refresh__icon">↻</span> Check Now
          </button>
          <button className="os-closed__override" onClick={toggleForce}>
            🔓 Force Open Board
          </button>
        </div>
      ) : (
        <main className="os-board">
          <OrderColumn stage="received" icon="📋" label="Order Received"   sublabel="Waiting for kitchen"      orders={received} emptyMsg="No new orders" />
          <OrderColumn stage="withchef" icon="👨‍🍳" label="With Chef"        sublabel="Being cooked for you"    orders={withchef} emptyMsg="Kitchen standing by" etaLabel={colEta('withchef', withchef)} />
          <OrderColumn stage="packing"  icon="📦" label="Being Packed"      sublabel="Almost ready!"            orders={packing}  emptyMsg="—" etaLabel={colEta('packing', packing)} />
          <OrderColumn stage="ready"    icon="✅" label="Ready for Pickup"  sublabel="Please collect at counter" orders={ready}    emptyMsg="—" onDismiss={dismiss} onPickedUp={markPickedUp} />
        </main>
      )}

      <Ticker />
    </div>
  );
}
